// src/utils/TCGapi/EnhancedTCGController.js
// Fixed with proper API integration and search handling
// May 2025

import TCGController from './TCGController';

class EnhancedTCGController extends TCGController {
    constructor() {
        super();
        this.customCards = [];
        this.isInitialized = false;
        this.STORAGE_KEY = 'tcg-deck-builder-custom-cards';
        this.customSetsKey = 'tcg-deck-builder-custom-sets';
    }

    /**
     * Initialize the enhanced controller
     */
    initialize() {
        if (this.isInitialized) return;
        
        console.log("EnhancedTCGController initializing...");
        this.loadCustomCards();
        this.isInitialized = true;
        console.log("EnhancedTCGController initialized.");
    }

    /**
     * Get all sets with availability information
     */
    async getAllSets() {
        try {
            const endpoint = 'https://api.pokemontcg.io/v2/sets';
            console.log(`Fetching all sets: ${endpoint}`);
            
            const response = await fetch(endpoint, {
                headers: { 'X-Api-Key': this.apiKey }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch sets: ${response.status}`);
            }
            
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error(`Error fetching sets: ${error.message}`);
            return [];
        }
    }

    /**
     * Get all rarities from the API
     */
    async getAllRarities() {
        try {
            const endpoint = 'https://api.pokemontcg.io/v2/rarities';
            console.log(`Fetching all rarities: ${endpoint}`);
            
            const response = await fetch(endpoint, {
                headers: { 'X-Api-Key': this.apiKey }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch rarities: ${response.status}`);
            }
            
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error(`Error fetching rarities: ${error.message}`);
            return [];
        }
    }

    /**
     * Get all subtypes from the API
     */
    async getAllSubtypes() {
        try {
            const endpoint = 'https://api.pokemontcg.io/v2/subtypes';
            console.log(`Fetching all subtypes: ${endpoint}`);
            
            const response = await fetch(endpoint, {
                headers: { 'X-Api-Key': this.apiKey }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch subtypes: ${response.status}`);
            }
            
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error(`Error fetching subtypes: ${error.message}`);
            return [];
        }
    }

    /**
     * Get custom card sets
     */
    getCustomSets() {
        try {
            const storedSets = localStorage.getItem(this.customSetsKey);
            if (!storedSets) return [];
            
            const parsedSets = JSON.parse(storedSets);
            if (!Array.isArray(parsedSets)) return [];
            
            return parsedSets;
        } catch (error) {
            console.error(`Error loading custom sets: ${error.message}`);
            return [];
        }
    }

    /**
     * Save custom card sets
     */
    saveCustomSets(sets) {
        try {
            localStorage.setItem(this.customSetsKey, JSON.stringify(sets));
        } catch (error) {
            console.error(`Error saving custom sets: ${error.message}`);
        }
    }

    /**
     * Add a new custom card
     * @param {Object} cardData - The card data to add
     */
    addCustomCard(cardData) {
        try {
            if (!this.isInitialized) {
                this.initialize();
            }
            
            // Generate ID if not present
            const id = cardData.id || `custom-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
            
            // Ensure essential properties
            const sanitizedCard = {
                id,
                name: cardData.name || 'Unnamed Card',
                supertype: cardData.supertype || 'Pokémon',
                image: cardData.image || cardData.imageUrl || '/assets/card-back.png',
                isCustom: true,
                customSetName: cardData.customSetName || null,
                ...cardData,
                id, // Ensure ID is retained
                isCustom: true, // Always mark as custom
            };
            
            // Add to memory
            this.customCards.push(sanitizedCard);
            
            // Save to storage
            this._saveCustomCards();
            
            return sanitizedCard;
        } catch (error) {
            console.error(`Error adding custom card: ${error.message}`);
            throw error;
        }
    }

    /**
     * Update a custom card
     * @param {string} cardId - The ID of the card to update
     * @param {Object} updatedData - The updated card data
     */
    updateCustomCard(cardId, updatedData) {
        try {
            if (!this.isInitialized) {
                this.initialize();
            }
            
            // Find the card
            const index = this.customCards.findIndex(card => card.id === cardId);
            if (index === -1) {
                throw new Error(`Card with ID ${cardId} not found`);
            }
            
            // Update the card
            this.customCards[index] = {
                ...this.customCards[index],
                ...updatedData,
                id: cardId, // Ensure ID doesn't change
                isCustom: true, // Always mark as custom
            };
            
            // Save to storage
            this._saveCustomCards();
            
            return this.customCards[index];
        } catch (error) {
            console.error(`Error updating custom card: ${error.message}`);
            throw error;
        }
    }

    /**
     * Delete a custom card
     * @param {string} cardId - The ID of the card to delete
     */
    deleteCustomCard(cardId) {
        try {
            if (!this.isInitialized) {
                this.initialize();
            }
            
            // Find and remove the card
            const initialLength = this.customCards.length;
            this.customCards = this.customCards.filter(card => card.id !== cardId);
            
            // If a card was removed, save changes
            if (this.customCards.length !== initialLength) {
                this._saveCustomCards();
                return true;
            }
            
            return false;
        } catch (error) {
            console.error(`Error deleting custom card: ${error.message}`);
            return false;
        }
    }

    /**
     * Get all custom cards
     */
    getCustomCards() {
        if (!this.isInitialized) {
            this.initialize();
        }
        
        return [...this.customCards];
    }

    /**
     * Get cards in a specific set
     * @param {string} setName - The name of the set to get cards from
     */
    getCardsInSet(setName) {
        if (!this.isInitialized) {
            this.initialize();
        }
        
        if (!setName) {
            // Return cards not in any set
            return this.customCards.filter(card => !card.customSetName);
        }
        
        return this.customCards.filter(card => card.customSetName === setName);
    }

    /**
     * Load custom cards from localStorage
     * @param {boolean} forceRefresh - Whether to force a refresh from localStorage
     */
    loadCustomCards(forceRefresh = false) {
        if (!forceRefresh && this.customCards.length > 0) {
            return this.customCards;
        }
        
        try {
            console.log("EnhancedTCGController: Loading custom cards from local storage...");
            
            // Get data from storage
            const storedData = localStorage.getItem(this.STORAGE_KEY);
            if (!storedData) {
                console.log("EnhancedTCGController: No custom cards found in local storage.");
                this.customCards = [];
                return [];
            }
            
            // Parse the stored data
            let customCards = [];
            
            try {
                // Check for chunked storage (for larger collections)
                const chunkMetadata = localStorage.getItem(`${this.STORAGE_KEY}-meta`);
                
                if (chunkMetadata) {
                    // Load chunked data
                    customCards = this._loadChunkedCustomCards(JSON.parse(chunkMetadata));
                } else {
                    console.log("EnhancedTCGController: No chunk metadata found. Attempting non-chunked load from main key.");
                    
                    // Try to parse as direct JSON
                    customCards = this._parseStoredCustomCards(storedData);
                }
            } catch (error) {
                console.error(`EnhancedTCGController: Error parsing custom cards: ${error.message}`);
                customCards = [];
            }
            
            // Update memory
            this.customCards = customCards;
            console.log(`EnhancedTCGController: Loaded ${customCards.length} custom cards into memory.`);
            
            return customCards;
        } catch (error) {
            console.error(`EnhancedTCGController: Error loading custom cards: ${error.message}`);
            this.customCards = [];
            return [];
        }
    }

    /**
     * Parse stored custom cards from JSON string
     * @private
     * @param {string} storedData - The stored JSON string
     */
    _parseStoredCustomCards(storedData) {
        try {
            const parsedData = JSON.parse(storedData);
            
            // Handle different storage formats
            if (Array.isArray(parsedData)) {
                console.log("EnhancedTCGController: Successfully parsed direct array of custom cards.");
                return parsedData;
            } else if (parsedData && parsedData._isCompressed && Array.isArray(parsedData.data)) {
                console.log("EnhancedTCGController: Detected old compressed wrapper object. Attempting to use its 'data' array.");
                return parsedData.data;
            } else if (parsedData && parsedData.data && Array.isArray(parsedData.data)) {
                console.log("EnhancedTCGController: Successfully parsed wrapped array of custom cards.");
                return parsedData.data;
            } else if (parsedData && parsedData.timestamp && parsedData.data && Array.isArray(parsedData.data)) {
                console.log("EnhancedTCGController: Successfully parsed timestamped wrapper of custom cards.");
                return parsedData.data;
            } else {
                console.log("EnhancedTCGController: Parsed custom cards from local storage is an object but not a recognized format (expected Array or specific wrapper). Value:", parsedData);
                
                // Try to convert object format to array if possible (compatibility with CustomCardManager)
                if (parsedData && typeof parsedData === 'object') {
                    // If it has a timestamp and data property, it might be from CustomCardManager
                    if (parsedData.timestamp && parsedData.data) {
                        return Array.isArray(parsedData.data) ? parsedData.data : [];
                    }
                    
                    // Try to convert object properties to array
                    const cardArray = [];
                    for (const key in parsedData) {
                        if (typeof parsedData[key] === 'object' && parsedData[key] !== null) {
                            cardArray.push(parsedData[key]);
                        }
                    }
                    
                    if (cardArray.length > 0) {
                        console.log(`EnhancedTCGController: Converted object format to array with ${cardArray.length} cards.`);
                        return cardArray;
                    }
                }
                
                return [];
            }
        } catch (error) {
            console.error(`EnhancedTCGController: Error parsing stored custom cards: ${error.message}`);
            return [];
        }
    }

    /**
     * Load chunked custom cards
     * @private
     * @param {Object} metadata - The chunk metadata
     */
    _loadChunkedCustomCards(metadata) {
        try {
            const { chunks, totalCards } = metadata;
            let allCards = [];
            
            // Load each chunk
            for (let i = 0; i < chunks; i++) {
                const chunkKey = `${this.STORAGE_KEY}-chunk-${i}`;
                const chunkData = localStorage.getItem(chunkKey);
                
                if (chunkData) {
                    try {
                        const parsedChunk = JSON.parse(chunkData);
                        if (Array.isArray(parsedChunk)) {
                            allCards = [...allCards, ...parsedChunk];
                        }
                    } catch (chunkError) {
                        console.error(`EnhancedTCGController: Error parsing chunk ${i}: ${chunkError.message}`);
                    }
                }
            }
            
            console.log(`EnhancedTCGController: Loaded ${allCards.length} of ${totalCards} cards from chunks.`);
            return allCards;
        } catch (error) {
            console.error(`EnhancedTCGController: Error loading chunked custom cards: ${error.message}`);
            return [];
        }
    }

    /**
     * Save custom cards to localStorage
     * @private
     */
    _saveCustomCards() {
        try {
            const MAX_STORAGE_SIZE = 5242880; // 5MB
            const CHUNK_SIZE = 1048576; // 1MB
            
            // Check if the data needs to be chunked
            const dataString = JSON.stringify(this.customCards);
            
            if (dataString.length < MAX_STORAGE_SIZE) {
                // Save directly
                localStorage.setItem(this.STORAGE_KEY, dataString);
                localStorage.removeItem(`${this.STORAGE_KEY}-meta`); // Clean up any old chunk metadata
                
                // Clean up any old chunks
                for (let i = 0; i < 10; i++) {
                    localStorage.removeItem(`${this.STORAGE_KEY}-chunk-${i}`);
                }
                
                return true;
            }
            
            // Need to chunk the data
            const chunks = Math.ceil(dataString.length / CHUNK_SIZE);
            const metadata = {
                chunks,
                totalCards: this.customCards.length,
                timestamp: Date.now(),
            };
            
            // Save metadata
            localStorage.setItem(`${this.STORAGE_KEY}-meta`, JSON.stringify(metadata));
            
            // Save chunks
            let chunkStart = 0;
            for (let i = 0; i < chunks; i++) {
                const cardsPerChunk = Math.ceil(this.customCards.length / chunks);
                const start = i * cardsPerChunk;
                const end = Math.min(start + cardsPerChunk, this.customCards.length);
                const chunkCards = this.customCards.slice(start, end);
                
                localStorage.setItem(`${this.STORAGE_KEY}-chunk-${i}`, JSON.stringify(chunkCards));
                chunkStart = end;
            }
            
            return true;
        } catch (error) {
            console.error(`EnhancedTCGController: Error saving custom cards: ${error.message}`);
            return false;
        }
    }

    /**
     * Override the query method to include custom cards when appropriate,
     * but ensure API calls are properly made for standard searches.
     * @param {Object} params - Query parameters
     */
    // Fix for EnhancedTCGController.js query method
async query(params = {}) {
    // Initialize if needed
    if (!this.isInitialized) {
        this.initialize();
    }
    
    // Check if this is a specific query for only custom cards
    if (params.customCardsOnly) {
        return { cards: this.customCards, totalCount: this.customCards.length, totalPages: 1 };
    }
    
    // For STANDARD API searches, use the parent method directly
    let apiResults = [];
    try {
        // Be careful not to modify original params when sending to API
        const apiParams = { ...params };
        
        // Convert name patterns from our internal format to the API's format
        if (apiParams.name) {
            if (apiParams.name.startsWith('*') && apiParams.name.endsWith('*')) {
                // Contains - need to convert to API format (remove *)
                apiParams.name = apiParams.name.substring(1, apiParams.name.length - 1);
            } else if (apiParams.name.startsWith('^')) {
                // Starts with - API needs different format  
                apiParams.name = apiParams.name.substring(1) + '*';
            }
            // API already supports exact match
        }

        // Logging the actual API query for debugging
        console.log("EnhancedTCGController: Sending API query:", apiParams);
        
        // Call super method to get API results
        const apiResponse = await super.query(apiParams);
        
        // Handle both array and pagination object responses
        if (Array.isArray(apiResponse)) {
            apiResults = apiResponse;
            console.log(`EnhancedTCGController: Received ${apiResults.length} results from API (array format)`);
        } else if (apiResponse && apiResponse.data) {
            apiResults = apiResponse.data;
            console.log(`EnhancedTCGController: Received ${apiResults.length} results from API (object format)`);
        }
    } catch (error) {
        console.error(`EnhancedTCGController: Error querying API: ${error.message}`);
        apiResults = [];
    }
    
    // If specifically excluding custom cards, return only API results in the expected format
    if (params.excludeCustomCards) {
        return { 
            cards: apiResults, 
            totalCount: apiResults.length, 
            totalPages: params.pageSize ? Math.ceil(apiResults.length / params.pageSize) : 1 
        };
    }
    
    // Otherwise, blend custom cards with API results
    let results = [...apiResults];
    
    // Filter custom cards based on query params
    const filteredCustomCards = this.customCards.filter(card => {
        // Filter by name
        if (params.name) {
            const nameLower = card.name.toLowerCase();
            const queryLower = params.name.toLowerCase();
            
            if (queryLower.startsWith('^')) {
                // Starts with
                const term = queryLower.substring(1);
                if (!nameLower.startsWith(term)) return false;
            } else if (queryLower.startsWith('*') && queryLower.endsWith('*')) {
                // Contains
                const term = queryLower.substring(1, queryLower.length - 1);
                if (!nameLower.includes(term)) return false;
            } else if (queryLower !== nameLower) {
                // Exact match
                return false;
            }
        }
        
        // Additional filters as before...
        return true;
    });
    
    // Add filtered custom cards to results
    results = [...results, ...filteredCustomCards];
    
    // For pagination, we need to ensure we're handling consistently
    if (params.page && params.pageSize) {
        const page = params.page || 1;
        const pageSize = params.pageSize || 10;
        const totalCount = results.length;
        const totalPages = Math.ceil(totalCount / pageSize);
        
        const start = (page - 1) * pageSize;
        const end = Math.min(start + pageSize, totalCount);
        const paginatedResults = results.slice(start, end);
        
        // Return with pagination metadata in a consistent format
        return {
            cards: paginatedResults,
            page: page,
            pageSize: pageSize,
            count: paginatedResults.length,
            totalCount: totalCount,
            totalPages: totalPages
        };
    }
    
    // Return in consistent format even without pagination
    return {
        cards: results,
        totalCount: results.length,
        totalPages: 1
    };
}
}

// Create and export a singleton instance
const enhancedTCGController = new EnhancedTCGController();
export default enhancedTCGController;